<template>
  <content-box :class="['referral-register', type, `step-${step}`]" :width="'428px'" :minHeight="'300px'">
    <template v-if="step === STEP.INPUT">
      <div class="referral-register-header">
        <i class="icon icon--logo-new-journeymap-red" />
        <h1 class="title">{{ $t(`referral.${type}.title`) }}</h1>
        <hr class="break">
        <button class="close-btn" @click="$emit('close')">
          <i class="icon icon--close-btn size-16"></i>
        </button>
      </div>
      <div ref="scrollArea" class="referral-register-body">
        <p class="description" v-html="$t(`referral.${type}.description`)"></p>
        <ui-input-text :type="'text'"
                        :id="'newCode'"
                        :class="{'error': referralCodeError}"
                        :placeholder="$t('referral.register.placeholder')"
                        :modelValue="newCode"
                        :modelError="referralCodeError"
                        @input="onReferralCodeInput"
                        @onFocus="referralCodeError = ''">
          {{ $t('referral.referralCode') }}
        </ui-input-text>
        <button class="get-code-button" :disabled="gotHeatmapCode" v-if="heatmapCode && heatmapCode !== currentCode" @click="getHeatmapCode">
          <span class="logo">UX Heatmap</span>
          <span>{{ $t('referral.getCode') }}</span>
          <i class="icon icon--star-badge" />
        </button>
        <template v-if="isTermRequired">
          <hr class="divider">
          <div class="term">
            <p class="term-title">
              <a class="link" :href="referralTermsUrl" target="_blank">{{ $t('referral.register.term.title') }}</a>
              <span class="necessary">{{ $t('referral.register.term.sub') }}</span>
            </p>
            <ul class="term-list">
              <li v-html="$t('referral.register.term.1')"></li>
              <li v-html="$t('referral.register.term.2')"></li>
              <li v-html="$t('referral.register.term.3')"></li>
            </ul>
          </div>
          <ui-checkbox :value="isTermAgreed" @input="onChangeCheckboxValue">
            {{ $t('referral.termConfirm') }}
          </ui-checkbox>
        </template>
        <hr class="break">
      </div>
      <div class="referral-register-footer" :class="{'has-scroll': hasScroll}">
        <button
          class="btf-button primary outline"
          :class="{ disabled: !isReadyToRegister }"
          @click="nextStep"
        >
          {{ type === 'register' ? $t('common.register') : $t('common.save') }}
        </button>
      </div>
    </template>
    <template v-else-if="step === STEP.CHECK">
      <div class="referral-register-header">
        <i v-if="type === 'register'" class="icon icon--dialog-warning" />
        <i v-else class="icon icon--logo-new-journeymap-red" />
        <h1 class="title" v-html="$t(`referral.${type}.checkTitle`)"></h1>
        <hr class="break">
        <button class="close-btn" @click="$emit('close')">
          <i class="icon icon--close-btn size-16"></i>
        </button>
      </div>
      <div ref="scrollArea" class="referral-register-body">
        <template v-if="type === 'modify'">
          <p v-if="newCode" class="description" v-html="$t(`referral.modify.checkDesc`)"></p>
          <div v-else class="stop-notice">
            <i class="icon icon--warning-white" />
            <p v-html="$t(`referral.modify.stopNotice`)"></p>
          </div>
        </template>
        <div class="code-box-wrapper">
          <div class="code">
            <span class="code-label">{{ $t('referral.currentCode') }}</span>
            <span class="code-value" v-if="type === 'register'">{{ heatmapCode ? heatmapCode : $t(`referral.noReferrer`) }}</span>
            <span class="code-value" v-auto-fit-text="{ min: 10, max: 16 }" v-else>{{ currentCode ? currentCode : $t(`referral.noReferrer`) }}</span>
          </div>
          <i class="icon icon--arrow-body-dark-gray"></i>
          <div class="code">
            <span class="code-label">{{ $t('referral.newCode') }}</span>
            <span class="code-value new" v-auto-fit-text="{ min: 10, max: 16 }">{{ newCode ? newCode : $t(`referral.modify.codeEmpty`) }}</span>
          </div>
        </div>
        <template v-if="type === 'modify' && newCode !== heatmapCode">
          <hr class="divider">
          <div class="heatmap-confirm">
            <p class="heatmap-confirm-title" v-html="$t(`referral.modify.heatmapConfirm`)"></p>
            <div class="radio-wrapper">
              <ui-radio :value="changeHeatmapCode"
                @input="changeHeatmapCode = true">
                {{ $t('common.yes') }}
              </ui-radio>
              <ui-radio :value="!changeHeatmapCode"
                @input="changeHeatmapCode = false">
                {{ $t('common.no') }}
              </ui-radio>
            </div>
            <p v-if="changeHeatmapCode" class="heatmap-code">
              <span>{{ $t('referral.currentCode') }} : </span>
              <span class="heatmap-code-value">{{ heatmapCode ? heatmapCode : $t(`referral.noReferrer`) }}</span>
            </p>
          </div>
        </template>
        <hr class="break">
      </div>
      <div class="referral-register-footer" :class="{'has-scroll': hasScroll}">
        <button class="btf-button outline" @click="type === 'register' ? register() : changeStep(STEP.INPUT)">
          {{ $t(`referral.${type}.goBack`) }}
        </button>
        <button
          class="btf-button primary outline" @click="type === 'register' ? register(true) : register()">
          {{ $t(`common.confirm`) }}
        </button>
      </div>
    </template>
    <template v-else-if="step === STEP.SUCCESS">
      <div ref="scrollArea" class="referral-register-body">
        <i class="icon icon--check-green-size50" />
        <h1 class="title" v-html="$t(`referral.${type}.successTitle`)"></h1>
        <p class="description" v-html="$t(`referral.${type}.successDesc`)"></p>
        <div class="applied-service">
          <hr class="divider">
          <p class="applied-service-label" v-html="$t(`referral.appliedService`)"></p>
          <div class="applied-service-list">
            <i class="icon icon--logo-new-journeymap-red" />
            <i v-if="changeHeatmapCode" class="icon icon--logo-new-heatmap-red"></i>
          </div>
        </div>
        <div class="empty"></div>
        <button class="btf-button outline" @click="$emit('close', true)">
          {{ $t('common.confirm') }}
        </button>
      </div>
    </template>
    <template v-else-if="step === STEP.FAIL">
      <div ref="scrollArea" class="referral-register-body">
        <i class="icon icon--dialog-warning" />
        <h1 class="title" v-html="$t(`referral.${type}.failTitle`)"></h1>
        <p class="description" v-html="$t(`referral.${type}.failDesc`)"></p>
        <p class="fail-contact">
          <span>{{ $t('referral.cxTeam') }}</span>
          <a :href="`mailto:${$t('referral.cxTeamEmail')}`">{{ $t('referral.cxTeamEmail') }}</a>
        </p>
        <div class="empty"></div>
        <button class="btf-button outline" @click="$emit('close', true)">
          {{ $t('common.confirm') }}
        </button>
      </div>
    </template>
  </content-box>
</template>

<script>
import { mapState } from 'vuex'
import { checkReferExist, checkReferValidate, setReferralCode } from '@/api/referral'

import ContentBox from '@/components/ui/ContentBox'
import UiCheckbox from '@/components/ui/UiCheckbox'
import UiInputText from '@/components/ui/UiInputText'
import UiRadio from '@/components/ui/UiRadio'

const STEP = Object.freeze({
  INPUT: 'input',
  CHECK: 'check',
  SUCCESS: 'success',
  FAIL: 'fail'
})

export default {
  components: {
    ContentBox,
    UiCheckbox,
    UiInputText,
    UiRadio
  },
  props: {
    type: {
      type: String,
      default: ''
    },
    currentCode: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      newCode: '',
      heatmapCode: '',
      gotHeatmapCode: false,
      referralCodeError: '',
      isTermRequired: false,
      isTermAgreed: false,
      hasScroll: false,
      STEP: STEP,
      step: STEP.INPUT,
      changeHeatmapCode: false
    }
  },
  computed: {
    ...mapState([
      'userData',
      'planData'
    ]),
    isReadyToRegister () {
      if (this.type === 'modify') {
        if (this.newCode === this.currentCode) return false
        return this.isTermRequired ? this.isTermAgreed : true
      }
      if (this.isTermRequired) return this.isTermAgreed && this.newCode
      return this.newCode
    },
    needCheck () {
      if (this.type === 'modify') return true
      if (this.newCode !== this.heatmapCode) return true
      return false
    },
    referralTermsUrl () {
      return `${process.env.VUE_APP_HOME_URL}/${this.$i18n.locale}/referralTerms`
    }
  },
  mounted () {
    this.checkScroll()
    window.addEventListener('resize', this.checkScroll)

    checkReferExist({ userId: this.userData.userId })
      .then(res => {
        this.heatmapCode = res.referral
        this.isTermRequired = !res.terms
      })
      .catch(e => {
        console.error(e)
      })

    if (this.type === 'modify' && this.currentCode) {
      this.newCode = this.currentCode
    }
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.checkScroll)
  },
  methods: {
    getHeatmapCode () {
      this.newCode = this.heatmapCode
      this.gotHeatmapCode = true
    },
    onReferralCodeInput (value) {
      this.newCode = value.trim()
      this.gotHeatmapCode = false
    },
    async nextStep () {
      if (this.type === 'modify' && !this.newCode) {
        this.changeStep(STEP.CHECK)
        return
      }

      let isReferralCodeValid = null

      await checkReferValidate({ referral: this.newCode, userId: this.userData.userId })
        .then(res => {
          isReferralCodeValid = res
        })
        .catch(e => {
          console.error(e)
        })

      if (isReferralCodeValid) {
        this.needCheck ? this.changeStep(STEP.CHECK) : this.register()
      } else {
        this.referralCodeError = isReferralCodeValid === false
          ? 'duplicatedReferralCode'
          : 'invalidReferralCode'
      }
    },
    register (applyHeatmap = false) {
      if (applyHeatmap) {
        this.changeHeatmapCode = true
      }
      setReferralCode({
        referral: this.newCode ? this.newCode : null,
        other: this.changeHeatmapCode,
        terms: this.isTermRequired ? this.isTermAgreed : null
      })
        .then(res => {
          if (res) {
            this.changeStep(STEP.SUCCESS)
          } else {
            this.changeStep(STEP.FAIL)
          }
        })
        .catch(e => {
          this.changeStep(STEP.FAIL)
        })
    },
    changeStep (step) {
      if (step === STEP.INPUT) {
        this.changeHeatmapCode = false
      }
      this.step = step
    },
    onChangeCheckboxValue (value) {
      this.isTermAgreed = value
    },
    checkScroll () {
      this.$nextTick(() => {
        const scrollArea = this.$refs.scrollArea
        this.hasScroll = scrollArea.scrollHeight > scrollArea.clientHeight
      })
    }
  }
}
</script>

<style lang="scss" scoped="scoped">
.referral-register {
  display: flex;
  flex-direction: column;
  max-height: calc(100% - 144px);
  min-height: 300px;
  width: 530px;
  padding: 30px 0 24px;
  border-radius: 14px;
  background-color: #fff;
  font-weight: 400;
  color: #2f2f2f;
  box-sizing: border-box;
  text-align: center;

  &-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 0 28px;
    .close-btn {
      display: flex;
      position: absolute;
      padding: 12px;
      top: 0;
      right: 0;
    }
  }

  &-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 0 28px;
    overflow-y: auto;
    .referral-code {
      font-weight: 600;
      font-size: 20px;
      line-height: 120%;
      color: #0E90A5;
    }
    .description {
      margin: 12px 0;
      font-size: 14px;
      line-height: 150%;
      color: #2f2f2f;
    }
    /deep/ .input--wrap {
      margin: 0;

      &.error {
        margin-bottom: 16px;
      }

      .validation-text {
        transition: none;
      }
    }
    .get-code-button {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 6px 8px 6px 10px;
      border-radius: 50px;
      border: 1px solid #BBB;
      background: #FFF;
      box-sizing: border-box;
      font-size: 12px;
      font-weight: 600;
      color: #444;
      cursor: pointer;
      .logo {
        color: $brandColor;
      }
      &:disabled {
        opacity: .3;
      }
    }
    .divider {
      margin: 16px 0;
      width: 100%;
      border: none;
      border-top: 1px dashed #d7d7d7;
      box-sizing: border-box;
    }
    .term {
      width: 100%;
      padding: 20px;
      border-radius: 5px;
      border: 1px solid #d7d7d7;
      background-color: #f4f4f4;
      box-sizing: border-box;
      text-align: left;
      &-title {
        margin-bottom: 8px;
        font-weight: 600;
        font-size: 12px;
        line-height: 150%;
        .link {
          text-decoration: underline;
          color: #444;
        }
        .necessary {
          font-weight: 400;
          color: $brandColor;
        }
      }
      &-list {
        display: flex;
        flex-direction: column;
        gap: 4px;
        color: #444;
        font-size: 12px;
        line-height: 1.5;
        list-style-type: disc;
        padding-left: 20px;
      }
    }

    & /deep/ .ui_checkbox {
      margin-left: 0;
      .text {
        color: #2f2f2f;
        font-size: 13px;
        font-weight: 400;
      }
    }

    .stop-notice {
      display: flex;
      width: 100%;
      padding: 8px 10px 8px 8px;
      align-items: center;
      gap: 8px;
      border-radius: 4px;
      background-color: #FCE515;
      box-sizing: border-box;
      text-align: left;
      color: #2F2F2F;
      font-size: 13px;
      line-height: 120%;
    }

    .code-box-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 12px;
      width: 100%;
      padding: 20px;
      border-radius: 5px;
      border: 1px solid #d7d7d7;
      background-color: #f4f4f4;
      box-sizing: border-box;
      .code {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        width: 136px;
        &-label {
          color: #666;
          font-size: 12px;
          font-weight: 500;
        }
        &-value {
          color: #2F2F2F;
          font-size: 16px;
          font-weight: 600;
          &.new {
            color: $brandColor;
          }
        }
      }
    }

    .heatmap-confirm-text {
      color: #2F2F2F;
      font-size: 14px;
      line-height: 150%;
      margin-bottom: 6px;
    }

    .heatmap-confirm {
      display: flex;
      flex-direction: column;
      gap: 16px;
      min-height: 61px;
      &-title {
        color: #666;
        font-size: 13px;
        font-weight: 400;
        line-height: 150%;
      }
      .radio-wrapper {
        @include flexXYcenter;
        height: 19px;
        .ui_radio {
          .text {
            font-size: 13px;
          }
        }
      }
      .heatmap-code {
        @include flexXYcenter;
        gap: 8px;
        padding: 6px 0;
        color: #666;
        font-size: 12px;
        font-weight: 500;
        &-value {
          color: #444;
          font-size: 16px;
        }
      }
    }

    .applied-service {
      width: 100%;
      padding-bottom: 20px;
      border-bottom: 1px dashed #d7d7d7;
      &-label {
        padding: 4px 0 20px;
        color: #2F2F2F;
        font-size: 13px;
        font-weight: 500;
      }
      &-list {
        @include flexXYcenter;
        gap: 28px;
      }
    }

    .fail-contact {
      color: #0074FF;
      line-height: 150%;
      a, a:-webkit-any-link {
        color: #0074FF;
        text-decoration: underline;
      }
    }
  }
  &-footer {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 24px;

    &.has-scroll {
      &:before {
        content: '';
        position: absolute;
        width: 100%;
        height: 12px;
        left: 0;
        bottom: 100%;
        z-index: 100;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.06) 100%);
      }
    }
  }

  &.step-success, &.step-fail, &.register.step-check {
    padding-top: 48px;
    .referral-register-body {
      .description {
        margin: 0;
      }
    }
  }

  .title {
    font-weight: 600;
    font-size: 20px;
    line-height: 120%;
    color: #444;
  }

  .btf-button {
    &.disabled {
      opacity: .33;
      filter: grayscale(0);
    }
  }

  .break {
    flex-shrink: 0;
    height: 1px;
    margin: 0;
    border: none;
    background: #d7d7d7;
  }

  .empty {
    width: 100%;
    height: 18px;
  }
}
</style>
